import {
  ContactModel,
  createContactModel,
  createPhoneNumber,
} from '@akin-home/contacts';

import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import {
  useSaveCommand,
  useUpdateScreenChatContext,
} from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { JSONText } from '$tablet-app/src/app/developer/components/JSONText';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { useCurrentProfile } from '$tablet-app/src/app/users';
import useBool from '$tablet-app/src/app/utils/state/useBool';
import { useCreateContactModel } from '@akin-home/contacts/react';
import { Text } from '@akin-home/text/native';
import { showInfoToast } from '@akin-home/toast/native';
import { UserModel } from '@akin-home/user';
import React, { useCallback, useEffect, useState } from 'react';
import { afterCreateContact } from '../../functions';
import { useUpdateContactFormListener } from '../../hooks';
import ContactFormScreenView from '../ContactFormScreen/ContactFormScreenView';

const context = {
  canSave: true,
  fields: {
    name: { type: 'text' },
    email: { type: 'email' },
    phone_number: { type: 'text' },
  },
};

const parameters = {
  defaults: {
    name: 'text',
    email: 'text',
    phone: {
      number: 'text',
      type: 'text',
    },
  },
};

export const CreateContactScreen = (
  props: RootStackScreenProps<'CreateContact'>
) => {
  const {
    navigation: { goBack },
    route: { params },
  } = props;

  const { profile } = useCurrentProfile();
  const { habitatID } = profile;

  useUpdateScreenChatContext(context);

  const [is_member, setIsMember] = useState(false);
  const [isSubmitting, setIsSubmitting, setNotSubmitting] = useBool();
  const [contact, setContact] = useState<ContactModel | undefined>(
    createContactModel({
      habitatID,
      name: params?.defaults?.name || '',
      emailAddresses: params?.defaults?.email ? [params?.defaults?.email] : [],
      phoneNumbers: params?.defaults?.phone
        ? [createPhoneNumber(params.defaults.phone)]
        : [],
    })
  );

  useUpdateContactFormListener(setContact);

  const create = useCreateContactModel();

  const onSubmit = useSaveCommand(
    useCallback(async () => {
      if (isSubmitting || !contact) return;

      setIsSubmitting();
      showInfoToast({ text1: 'Saving Contact...' });

      const { sub, username } = useStateStore.getState()
        .current_user as UserModel;

      try {
        const { data } = await create.mutateAsync({
          contact,
          create_linked_user: is_member
            ? {
                sub,
                username,
              }
            : undefined,
        });

        afterCreateContact(data);
      } catch (err) {
        setNotSubmitting();
      }

      goBack();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitting, contact, profile.id, is_member])
  );

  useEffect(() => {
    sendBackgroundMessage(intent_labels.open_create_contact_screen);
  }, []);

  return contact ? (
    <>
      <ContactFormScreenView
        contact={contact}
        submitting={isSubmitting}
        is_member={is_member}
        setIsMember={setIsMember}
        onSubmit={onSubmit}
        onChange={setContact}
        onCancel={goBack}
      />

      <DeveloperScreenInfo
        context={<JSONText>{context}</JSONText>}
        parameters={<Text>{JSON.stringify(parameters, null, 3)}</Text>}
      />
    </>
  ) : null;
};
